import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";

import {TranslateService} from "@ngx-translate/core";

import {AuthenticationService} from "@shared/services/authentication/authentication.service";

import {AuthenticateUserRequest} from "@commons/models/remote.request/AuthenticateUserRequest";
import {SetUserEmailRequest} from "@commons/models/remote.request/SetUserEmailRequest";
import {AuthenticateUserResponse} from '@commons/models/remote.response/AuthenticateUserResponse';
import {WebAccessService} from '@commons/services/web.access/web-access.service';

import {NewNavigationService} from "@shared/services/new-navigation.service";
import {BaseComponent} from "@shared/components/base-component";

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.less']
})
export class CreateAccountComponent extends BaseComponent implements OnInit, OnDestroy {

  private readonly requiredErrorKey = 'required';
  private readonly emailErrorKey = 'email';
  private readonly emailsNotEqualErrorKey = 'emailsNotEqual';
  private readonly emailAlreadyInUseErrorKey = 'emailAlreadyInUse';

  fieldIsRequiredErrorMessage = '';
  emailFormatIncorrectErrorMessage = '';
  emailsNotEqualErrorMessage = '';
  emailAlreadyInUseErrorMessage = '';

  isLoading = false;
  showAccountDataError = false;
  showEmailAddressInUse = false;
  isRawPasswordVisible = false;
  inputType = 'password';

  idCtrl = new FormControl('', [Validators.required]);
  loginCtrl = new FormControl('', [Validators.required]);
  passwordCtrl = new FormControl('', [Validators.required]);
  emailCtrl = new FormControl('', [Validators.required, Validators.email, this.emailsEqualValidator(), this.emailAlreadyInUseValidator()]);
  emailRepeatCtrl = new FormControl('', [Validators.required, Validators.email, this.emailsEqualValidator(), this.emailAlreadyInUseValidator()]);

  form = this.formBuilder.group({
    id: this.idCtrl,
    login: this.loginCtrl,
    password: this.passwordCtrl,
    email: this.emailCtrl,
    emailRepeat: this.emailRepeatCtrl,
  });

  get isVisibleShowPasswordButton(): boolean {
    return this.passwordCtrl.value != null && this.passwordCtrl.value.length > 0;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private navigationService: NewNavigationService,
    private webAccessService: WebAccessService,
    private translateService: TranslateService
  ) {
    super();
    this.setTranslations();
  }

  private setTranslations() {
    this.translateService.get('errors.field-is-required').subscribe(t => this.fieldIsRequiredErrorMessage = t);
    this.translateService.get('errors.email-format-incorrect').subscribe(t => this.emailFormatIncorrectErrorMessage = t);
    this.translateService.get('errors.emails-not-equal').subscribe(t => this.emailsNotEqualErrorMessage = t)
    this.translateService.get('errors.email-already-in-use').subscribe(t => this.emailAlreadyInUseErrorMessage = t);
  }

  ngOnInit(): void {
    this.subscribeToFormValueChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  onShowPasswords() {
    this.isRawPasswordVisible = true;
    this.inputType = 'text';
  }

  onHidePasswords() {
    this.isRawPasswordVisible = false;
    this.inputType = 'password';
  }

  onSubmit() {
    this.createAccount(this.emailCtrl.value!);
  }

  onNavigateToLogin() {
    this.navigationService.toLogin();
  }

  getErrorMessage(control: AbstractControl): string {
    if (control.hasError(this.requiredErrorKey))
      return this.fieldIsRequiredErrorMessage;

    if (control.hasError(this.emailErrorKey))
      return this.emailFormatIncorrectErrorMessage;

    if (control.hasError(this.emailsNotEqualErrorKey))
      return this.emailsNotEqualErrorMessage;

    if (control.hasError(this.emailAlreadyInUseErrorKey))
      return this.emailAlreadyInUseErrorMessage;

    return "";
  }

  getErrorMessageElementId(controlName: string, control: AbstractControl): string {
    let errorName = '';

    if (control.hasError(this.requiredErrorKey))
      errorName = this.requiredErrorKey;
    else if (control.hasError(this.emailErrorKey))
      errorName = this.emailErrorKey;
    else if (control.hasError(this.emailsNotEqualErrorKey))
      errorName = this.emailsNotEqualErrorKey;
    else if (control.hasError(this.emailAlreadyInUseErrorKey))
      errorName = this.emailAlreadyInUseErrorKey;

    return `${controlName}-error-${errorName}`;
  }

  private emailsEqualValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const areEmailsNotEqual =
        this.form != null &&
        this.form.controls != null &&
        this.emailCtrl.value !== this.emailRepeatCtrl.value;

      return areEmailsNotEqual ? {emailsNotEqual: true} : null;
    };
  }

  private emailAlreadyInUseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.showEmailAddressInUse ? {emailAlreadyInUse: true} : null;
    };
  }

  private checkIfEmailsNotEqual() {
    const notEqual = this.emailCtrl.value !== this.emailRepeatCtrl.value;

    this.emailCtrl.setErrors({emailsNotEqual: notEqual ?? null});
    this.emailCtrl.updateValueAndValidity({emitEvent: false});

    this.emailRepeatCtrl.setErrors({emailsNotEqual: notEqual ?? null});
    this.emailRepeatCtrl.updateValueAndValidity({emitEvent: false});
  }

  private getAuthenticateUserRequest() {
    return {
      DB: this.idCtrl.value,
      Login: this.loginCtrl.value,
      Password: this.passwordCtrl.value,
      SessionId: undefined
    } as AuthenticateUserRequest;
  }

  private createAccount(email: string) {
    this.form.markAllAsTouched();

    if (this.form.invalid)
      return;

    this.isLoading = true;
    this.showAccountDataError = false;

    this.authenticationService.authenticate(this.getAuthenticateUserRequest())
      .subscribe({
        next: response => this.afterUserIsAuthenticated(response, email),
        error: () => this.authenticationError()
      });
  }

  private afterUserIsAuthenticated(result: AuthenticateUserResponse, email: string) {
    if (!result)
      return;

    this.setUserEmail(email);
  }

  private authenticationError() {
    this.showAccountDataError = true;
    this.isLoading = false;
  }

  private setUserEmail(email: string) {
    const request = {Email: email} as SetUserEmailRequest;

    this.webAccessService.SetUserEmail(request).subscribe({
      next: response => this.afterUserEmailSet(response),
      error: () => this.logout(),
    });
  }

  private afterUserEmailSet(result: any) {
    if (!result.Result.IsChangeSuccessful) {
      this.notSuccessfulUserEmailSet();
      return;
    }

    this.logout();
    this.navigationService.toCreateAccountConfirmation();
  }

  private notSuccessfulUserEmailSet() {
    this.emailCtrl.setErrors({emailAlreadyInUse: true});
    this.emailRepeatCtrl.setErrors({emailAlreadyInUse: true});
    this.form.updateValueAndValidity({emitEvent: false});

    this.showEmailAddressInUse = true;
    this.logout();
  }

  private logout() {
    this.authenticationService.closeSession().subscribe();
    this.isLoading = false;
  }

  private subscribeToFormValueChanges() {
    this.addToUnsubscribe(
      this.form.valueChanges.subscribe(() => {
        this.showEmailAddressInUse = false;
        this.checkIfEmailsNotEqual();
      }));
  }

}
